// Hardcoded FC code mapping for vehicle ID generation
// Format: "ST_City" (e.g., "IL_Chicago", "TX_Dallas")
const FC_CODE_MAP = {
  IL_Chicago: { stateCode: 'il', cityCode: 'chi' },
  'CA_Los Angeles': { stateCode: 'ca', cityCode: 'la' },
  WI_Milwaukee: { stateCode: 'wi', cityCode: 'mke' },
  IN_Indianapolis: { stateCode: 'in', cityCode: 'ind' },
  MN_Minneapolis: { stateCode: 'mn', cityCode: 'msp' },
  MI_Detroit: { stateCode: 'mi', cityCode: 'det' },
  TX_Dallas: { stateCode: 'tx', cityCode: 'dal' },
  TX_Houston: { stateCode: 'tx', cityCode: 'hou' },
  TX_Austin: { stateCode: 'tx', cityCode: 'aus' },
  CO_Denver: { stateCode: 'co', cityCode: 'den' },
  AZ_Phoenix: { stateCode: 'az', cityCode: 'phx' },
  GA_Atlanta: { stateCode: 'ga', cityCode: 'atl' },
  TN_Nashville: { stateCode: 'tn', cityCode: 'nas' },
  'MO_St. Louis': { stateCode: 'mo', cityCode: 'stl' },
  'MO_Kansas City': { stateCode: 'mo', cityCode: 'kc' },
  OH_Cincinnati: { stateCode: 'oh', cityCode: 'cin' },
  OH_Columbus: { stateCode: 'oh', cityCode: 'col' },
  OH_Cleveland: { stateCode: 'oh', cityCode: 'cle' },
};

// Day of week abbreviations
const DAY_ABBREV = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Resolve the state and city codes for a fulfillment center name.
 * Tries FC_CODE_MAP directly, then with the state prefix stripped, then falls
 * back to 'xx' / lowercased city string with spaces removed.
 *
 * @param {string} fc  e.g. "IL_Chicago" or "CA_Los Angeles"
 * @returns {{ stateCode: string, cityCode: string }}
 */
const resolveFcCodes = (fc) => {
  // Direct lookup
  if (FC_CODE_MAP[fc]) return FC_CODE_MAP[fc];

  // Fallback: strip leading "ST_" prefix, then look up the remainder
  const normalizedFc = fc.includes('_') ? fc.split('_').slice(1).join('_') : fc;
  if (FC_CODE_MAP[normalizedFc]) return FC_CODE_MAP[normalizedFc];

  // Final fallback
  return {
    stateCode: 'xx',
    cityCode: normalizedFc.toLowerCase().replace(/\s+/g, ''),
  };
};

/**
 * Format a traffic config object (with weekday/weekend arrays of magnifier
 * windows) into a flat array. Includes endTime for display purposes (e.g. the
 * submission confirmation summary); the API shape omits it — use
 * formatTrafficConfigForApi for anything sent to the backend.
 *
 * @param {string} fc  Fulfillment center name
 * @param {{ weekday: Array, weekend: Array }} trafficConfig
 * @returns {Array<{ fulfillmentCenter: string, dayType: string, startTime: string, endTime: string, trafficMultiplier: number }>}
 */
const formatTrafficConfigForDisplay = (fc, trafficConfig) => {
  const config = trafficConfig || { weekday: [], weekend: [] };
  return [
    ...(config.weekday || []).map((window) => ({
      fulfillmentCenter: fc,
      dayType: 'weekday',
      startTime: window.startTime?.substring(0, 5),
      endTime: window.endTime?.substring(0, 5),
      trafficMultiplier: Number.parseFloat(window.magnifier),
    })),
    ...(config.weekend || []).map((window) => ({
      fulfillmentCenter: fc,
      dayType: 'weekend',
      startTime: window.startTime?.substring(0, 5),
      endTime: window.endTime?.substring(0, 5),
      trafficMultiplier: Number.parseFloat(window.magnifier),
    })),
  ];
};

/**
 * Format a traffic config object into the flat array shape expected by the API.
 * endTime is intentionally omitted — it is not part of the
 * UpdateTrafficConfigurationInput / run submission schema.
 *
 * @param {string} fc  Fulfillment center name
 * @param {{ weekday: Array, weekend: Array }} trafficConfig
 * @returns {Array<{ fulfillmentCenter: string, dayType: string, startTime: string, trafficMultiplier: number }>}
 */
const formatTrafficConfigForApi = (fc, trafficConfig) =>
  formatTrafficConfigForDisplay(fc, trafficConfig).map(
    ({ endTime, ...rest }) => rest,
  );

export {
  FC_CODE_MAP,
  DAY_ABBREV,
  resolveFcCodes,
  formatTrafficConfigForDisplay,
  formatTrafficConfigForApi,
};
